import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, ErrorState, LoadingBlock, PageHeader, Select, StatusDot, TableWrap } from '@/components/ui';
import { formatDateTime, sessionTone, SESSION_STATUS_LABEL } from '@/lib/format';
import { copy, useI18n } from '@/lib/i18n';
import type { SessionItem } from '@shared/index';
import './CustomerPages.css';

interface UsageReport {
  summary: {
    totalSessions: number;
    completedSessions: number;
    scheduledSessions: number;
    runningSessions: number;
    failedSessions: number;
    actualRounds: number;
    participants: number;
    ambientEvents: number;
    creditsReserved: number;
    creditsRefunded: number;
    creditsConsumed: number;
  };
  daily: Array<{ day: string; sessions: number; rounds: number; participants: number }>;
  byProduct: Array<{ productName: string; sessions: number; rounds: number; participants: number }>;
}

/** Gerçek oturum verisinden türetilen operasyon özeti; uydurma performans metriği yoktur. */
export default function Reports() {
  const { language, locale } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);
  const [rangeDays, setRangeDays] = useState(30);
  const range = useMemo(() => {
    const to = new Date();
    const from = new Date(to);
    from.setDate(from.getDate() - rangeDays);
    return { from, to };
  }, [rangeDays]);
  const sessions = useQuery({
    queryKey: ['sessions', 'report', range.from.toISOString(), range.to.toISOString()],
    queryFn: () => api.get<{ items: SessionItem[]; total: number }>('/sessions', { from: range.from.toISOString(), to: range.to.toISOString(), pageSize: 100 }),
  });
  const usage = useQuery({
    queryKey: ['reports', 'usage', range.from.toISOString(), range.to.toISOString()],
    queryFn: () => api.get<UsageReport>('/reports/usage', { from: range.from.toISOString(), to: range.to.toISOString() }),
  });
  const items = sessions.data?.items ?? [];
  const summary = usage.data?.summary;
  const completed = summary?.completedSessions ?? 0;
  const attention = summary?.failedSessions ?? 0;
  const scheduled = summary?.scheduledSessions ?? 0;
  const chartDays = useMemo(() => Array.from({ length: 14 }, (_, index) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - (13 - index));
    const key = date.toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' });
    const source = usage.data?.daily.find((item) => item.day === key);
    return { key, count: source?.rounds ?? 0, label: date.toLocaleDateString(locale, { day: 'numeric', month: 'short' }) };
  }), [usage.data?.daily, locale]);
  const chartMax = Math.max(1, ...chartDays.map((day) => day.count));

  return (
    <div className="customer-page report-page stack stack--lg">
      <PageHeader
        title={c('Raporlar', 'Reports')}
        description={c('Cihaz ve içerik operasyonunu gerçek oturum verileriyle inceleyin.', 'Review device and content operations using actual session data.')}
        actions={<Select aria-label={c('Rapor tarih aralığı', 'Report date range')} value={rangeDays} onChange={(event) => setRangeDays(Number(event.target.value))}><option value={7}>{c('Son 7 gün', 'Last 7 days')}</option><option value={30}>{c('Son 30 gün', 'Last 30 days')}</option><option value={90}>{c('Son 90 gün', 'Last 90 days')}</option></Select>}
      />
      {sessions.isLoading || usage.isLoading ? <LoadingBlock rows={5} label={c('Rapor yükleniyor', 'Loading report')} /> : sessions.isError || usage.isError ? <ErrorState message={c('Rapor verisi alınamadı.', 'Report data could not be loaded.')} onRetry={() => { void sessions.refetch(); void usage.refetch(); }} /> : (
        <>
          <dl className="report-summary" aria-label={c('Oturum özeti', 'Session summary')}>
            <div><dt>{c('Gerçek tur', 'Actual rounds')}</dt><dd>{summary?.actualRounds ?? 0}</dd><span>{c('Ajan tarafından doğrulandı', 'Verified by the agent')}</span></div>
            <div><dt>{c('Katılımcı', 'Participants')}</dt><dd>{summary?.participants ?? 0}</dd><span>{c('Kredi hesabından bağımsız', 'Independent of credit usage')}</span></div>
            <div><dt>{c('Tüketilen kredi', 'Credits consumed')}</dt><dd>{summary?.creditsConsumed ?? 0}</dd><span>{c(`${summary?.creditsRefunded ?? 0} kredi iade edildi`, `${summary?.creditsRefunded ?? 0} credits refunded`)}</span></div>
            <div><dt>{c('Toplam oturum', 'Total sessions')}</dt><dd>{summary?.totalSessions ?? 0}</dd><span>{c(`${summary?.ambientEvents ?? 0} ücretsiz ambient olayı`, `${summary?.ambientEvents ?? 0} free ambient events`)}</span></div>
          </dl>

          <div className="report-visuals">
            <section className="report-panel report-panel--chart" aria-labelledby="activity-chart-title">
              <header><div><h2 id="activity-chart-title">{c('Tur hareketi', 'Round activity')}</h2><p>{c('Son 14 gündeki doğrulanmış etkileşimli turlar', 'Verified interactive rounds over the last 14 days')}</p></div><strong className="num">{chartDays.reduce((sum, day) => sum + day.count, 0)}</strong></header>
              <div className="report-bars" role="img" aria-label={c('Son 14 gündeki tur sayıları', 'Round counts over the last 14 days')}>
                {chartDays.map((day) => <div className="report-bar" key={day.key} title={`${day.label}: ${day.count} ${c('tur', 'rounds')}`}><span style={{ ['--bar' as string]: day.count / chartMax }} /><small>{day.label}</small></div>)}
              </div>
            </section>
            <section className="report-panel" aria-labelledby="status-title">
              <header><div><h2 id="status-title">{c('Durum dağılımı', 'Status distribution')}</h2><p>{c('Seçili aralıktaki operasyon durumu', 'Operation status in the selected range')}</p></div></header>
              <dl className="report-statuses">
                <div><dt><span className="report-statusdot report-statusdot--success" />{c('Tamamlanan', 'Completed')}</dt><dd className="num">{completed}</dd></div>
                <div><dt><span className="report-statusdot report-statusdot--primary" />{c('Planlanan', 'Scheduled')}</dt><dd className="num">{scheduled}</dd></div>
                <div><dt><span className="report-statusdot report-statusdot--warning" />{c('Aktif', 'Active')}</dt><dd className="num">{summary?.runningSessions ?? 0}</dd></div>
                <div><dt><span className="report-statusdot report-statusdot--danger" />{c('İncelenecek', 'Needs review')}</dt><dd className="num">{attention}</dd></div>
              </dl>
            </section>
          </div>
          <Card title={c('Son oturumlar', 'Recent sessions')} padded={false}>
            <TableWrap caption={c('Son 30 gündeki oturumlar', 'Sessions in the last 30 days')}>
              <thead><tr><th>{c('Başlangıç', 'Start')}</th><th>{c('İçerik', 'Content')}</th><th>{c('Nokta', 'Endpoint')}</th><th>{c('Durum', 'Status')}</th><th className="table__num">{c('Tur', 'Rounds')}</th><th className="table__num">{c('Kredi', 'Credits')}</th></tr></thead>
              <tbody>{items.slice(0, 25).map((item) => <tr key={item.id}><td>{formatDateTime(item.startsAt)}</td><td>{item.productName}</td><td>{item.endpointName}</td><td><StatusDot tone={sessionTone(item.status) ?? 'neutral'} label={language === 'tr' ? SESSION_STATUS_LABEL[item.status] ?? item.status : sessionStatusEnglish(item.status)} /></td><td className="table__num">{item.actualRounds}</td><td className="table__num">{item.creditsSpent}</td></tr>)}</tbody>
            </TableWrap>
            {items.length === 0 && <p className="customer-emptyline">{c('Bu tarih aralığında oturum kaydı yok. Yeni kayıtlar oluştuğunda grafik ve tablo birlikte güncellenir.', 'There are no sessions in this date range. The chart and table will update together when new records are created.')}</p>}
          </Card>
        </>
      )}
    </div>
  );
}

function sessionStatusEnglish(status: string): string {
  return ({ scheduled: 'Scheduled', running: 'Running', completed: 'Completed', stopped: 'Stopped', failed: 'Failed' } as Record<string, string>)[status] ?? status;
}
